import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import type { CapabilityGapAnalysis } from '../../types/analytics';

interface CapabilityGapChartProps {
  data: CapabilityGapAnalysis[];
}

export const CapabilityGapChart: React.FC<CapabilityGapChartProps> = ({ data }) => {
  const getStatusColor = (percentage: number) => {
    if (percentage >= 80) return 'text-sage-600';
    if (percentage >= 50) return 'text-[var(--mustard)]';
    return 'text-coral-600';
  };

  const getBarColor = (percentage: number) => {
    if (percentage >= 80) return 'from-sage-400 to-sage-600';
    if (percentage >= 50) return 'from-amber-400 to-amber-600';
    return 'from-coral-400 to-coral-600';
  };

  return (
    <div className="space-y-6">
      {data.map((gap) => (
        <div key={gap.mofLevel} className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {gap.coveragePercentage >= 80 ? (
                <CheckCircle className={`h-5 w-5 ${getStatusColor(gap.coveragePercentage)}`} />
              ) : (
                <AlertCircle className={`h-5 w-5 ${getStatusColor(gap.coveragePercentage)}`} />
              )}
              <h4 className="font-semibold text-charcoal-900">{gap.mofLevel}</h4>
            </div>
            <span className={`text-sm font-bold ${getStatusColor(gap.coveragePercentage)}`}>
              {gap.coveragePercentage}% Complete
            </span>
          </div>

          {/* Progress bar */}
          <div className="relative h-8 bg-[var(--hover-tint)] overflow-hidden">
            <div
              className={`absolute left-0 top-0 h-full bg-gradient-to-r ${getBarColor(gap.coveragePercentage)} transition-all duration-500`}
              style={{ width: `${gap.coveragePercentage}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-between px-3 text-sm">
              <span className="text-charcoal-700 font-medium">
                {gap.visitedCount} of {gap.totalCount} frameworks
              </span>
            </div>
          </div>

          {/* Unvisited frameworks */}
          {gap.unvisitedFrameworks.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-charcoal-600 hover:text-charcoal-900">
                {gap.unvisitedFrameworks.length} unvisited framework{gap.unvisitedFrameworks.length > 1 ? 's' : ''}
              </summary>
              <ul className="mt-2 ml-4 space-y-1 text-charcoal-700">
                {gap.unvisitedFrameworks.map((name, idx) => (
                  <li key={idx} className="list-disc">
                    {name}
                  </li>
                ))}
                {gap.totalCount - gap.visitedCount > gap.unvisitedFrameworks.length && (
                  <li className="text-charcoal-500 italic">
                    +{gap.totalCount - gap.visitedCount - gap.unvisitedFrameworks.length} more...
                  </li>
                )}
              </ul>
            </details>
          )}
        </div>
      ))}
      
      {data.length === 0 && (
        <div className="text-center py-8 text-charcoal-500">
          <p>No capability data available yet.</p>
          <p className="text-sm mt-2">Start exploring the knowledge system!</p>
        </div>
      )}
    </div>
  );
};

